import { EnumTipoDomandaRequestModalitaRiepilogo } from '@framework/api-client/api/inquiries-compile/schemas/enumTipoDomandaRequestModalitaRiepilogo.ts';
import { type PersonalizzazioniRiepilogoDTO } from '@framework/api-client/api/inquiries-compile/schemas/personalizzazioniRiepilogoDTO.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoDomandaDTO.ts';
import { SubmissionHeadingTitle } from '@framework/common-web/ui/inquiry/submission/SubmissionHeadingTitle.tsx';
import { SubmissionPaymentStateTag } from '@framework/frontoffice-submission/burden/SubmissionPaymentStateTag.tsx';
import { ExtendedSubmissionSummary } from '@framework/frontoffice-submission/submission/summary/extended/ExtendedSubmissionSummary.tsx';
import {
  type GetSummaryModuleLink,
  SummaryModuleLinkProvider,
} from '@framework/frontoffice-submission/submission/summary/extended/SummaryModuleLinkContext.tsx';
import { SimpleSubmissionSummary } from '@framework/frontoffice-submission/submission/summary/simple/SimpleSubmissionSummary.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { generatePath } from '@framework/ui/core/router/TypedRoute.tsx';
import clx from 'classnames';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SubmissionModuleSummary } from './summary-items/SubmissionSummaryModule.tsx';

/**
 * Card condizionale per il riepilogo: in modalità estesa nasconde
 * bordi, padding e ombra per lasciare il layout ai componenti interni.
 */
const SummaryConditionalCard = styled(ResponsiveCard)`
  border: 1px solid ${props => props.theme.colorSplit};
  font-size: 18px;
  &.extended-summary {
    border: none;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    > .ant-card-body {
      padding: 0;
      border-radius: 0;
    }
  }
`;

interface ISubmissionSummaryProps {
  text?: string | null;
  submission?: RichiestaDTO;
  modules?: RichiestaModuloDTO[];
  /** Se c'è più di un modulo, mostriamo il tasto di download per ogni modulo */
  hasInlineDownload?: boolean;
  /** ID della richiesta */
  submissionId?: string;
  /** Tipo domanda per dati generali */
  inquiryType?: TipoDomandaDTO;
  /** Personalizzazioni del riepilogo per visibilità/etichette delle sezioni */
  customizations?: PersonalizzazioniRiepilogoDTO | null;
  /**
   * Generatore del link "Vai al modulo" del riepilogo esteso. Di default punta
   * alla rotta di compilazione; la vista della richiesta inviata inietta la
   * rotta in sola lettura (`.../inviata/moduli/...`).
   */
  getModuleLink?: GetSummaryModuleLink;
}

export function SubmissionSummary(props: ISubmissionSummaryProps) {
  const {
    text,
    submission,
    modules,
    hasInlineDownload,
    submissionId,
    inquiryType,
    customizations,
    getModuleLink,
  } = props;
  const { t } = useTranslation();

  const isExtended =
    inquiryType?.modalitaRiepilogo ===
    EnumTipoDomandaRequestModalitaRiepilogo.ESTESO;

  return (
    <SummaryConditionalCard className={clx({ 'extended-summary': isExtended })}>
      <SubmissionHeadingTitle level={2}>
        {t(
          'submission-summary.riepilogo-richiesta',
          'Riepilogo della Richiesta',
        )}
      </SubmissionHeadingTitle>
      {isExtended && submissionId ? (
        <SummaryModuleLinkProvider
          getModuleLink={
            getModuleLink ??
            ((sid, mid) =>
              generatePath('/richieste/:id/moduli/:moduleId', {
                id: sid,
                moduleId: mid,
              }))
          }
        >
          <ExtendedSubmissionSummary
            text={text}
            submission={submission}
            modules={modules}
            submissionId={submissionId}
            inquiryType={inquiryType}
            customizations={customizations}
          />
        </SummaryModuleLinkProvider>
      ) : (
        <SimpleSubmissionSummary
          text={text}
          submission={submission}
          modules={modules}
          hasInlineDownload={hasInlineDownload}
          PaymentStateTag={SubmissionPaymentStateTag}
          ModuleSummary={SubmissionModuleSummary}
        />
      )}
    </SummaryConditionalCard>
  );
}
